import React, { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PaymentReviewDialog — Opens after a parent (or admin) books a batch of
 * PAYG sessions. Groups every pending booking into ONE Mollie charge:
 *
 *   • Itemised summary (child × session × date × unit price)
 *   • Sibling discount as a negative line item (if enabled + applicable)
 *   • Single total
 *   • Card OR bank transfer — one basket, one payment
 */
export function PaymentReviewDialog({ open, onClose, intent, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('choose'); // 'choose' | 'bank'
  const [bank, setBank] = useState(null); // { reference, amount, bank_details }

  if (!open || !intent) return null;

  const items = intent.line_items || [];
  const total = Number(intent.total || intent.total_amount || 0);
  const currency = intent.currency || 'GBP';
  const symbol = currency === 'GBP' ? '£' : currency + ' ';

  const handleCardPay = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await api.startPaymentIntentMollie(intent.intent_id);
      if (!res?.checkout_url) throw new Error('Card gateway returned no URL');
      window.location.href = res.checkout_url;
    } catch (err) {
      const msg = err?.message || 'Card payment failed';
      setError(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  const handleBankPay = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await api.startPaymentIntentBankTransfer(intent.intent_id);
      setBank(res);
      setPhase('bank');
      setBusy(false);
    } catch (err) {
      const msg = err?.message || 'Bank transfer setup failed';
      setError(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="payment-review-dialog"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 220,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div style={{
        width: 'min(560px,100%)', maxHeight: '92vh', background: 'hsl(var(--background))',
        borderRadius: 12, boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }} data-testid="payment-review-title">
              {phase === 'bank' ? 'Bank transfer details' : 'Review & pay'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
              {items.filter((i) => i.kind !== 'discount').length} booking{items.filter((i) => i.kind !== 'discount').length === 1 ? '' : 's'} — one charge, one receipt
            </div>
          </div>
          <button type="button" onClick={handleClose} className="btn btn-ghost"
            aria-label="Close" data-testid="payment-review-close" disabled={busy}
            style={{ padding: '0.35rem 0.6rem', fontSize: '1.05rem' }}>✕</button>
        </div>

        {phase === 'bank' && bank ? (
          <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: '1 1 auto' }} data-testid="payment-review-bank-details">
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
              Please transfer <strong>{symbol}{Number(bank.amount).toFixed(2)}</strong> to the account below and quote the reference. Your bookings will be confirmed once the nursery marks the payment received.
            </p>
            <div className="card" style={{ padding: '0.85rem', marginBottom: '0.75rem' }}>
              <Row label="Account name" value={bank.bank_details?.account_name} />
              <Row label="Sort code" value={bank.bank_details?.sort_code} mono />
              <Row label="Account number" value={bank.bank_details?.account_number} mono />
              <Row label="Reference" value={bank.reference} mono highlight />
              <Row label="Amount" value={`${symbol}${Number(bank.amount).toFixed(2)}`} highlight />
            </div>
            <button type="button" onClick={() => { onClose?.(); onPaid?.({ deferred: true }); }}
              className="btn btn-primary" data-testid="payment-review-bank-done"
              style={{ width: '100%' }}>Done — I'll transfer now</button>
          </div>
        ) : (
          <>
            <div style={{ padding: '0.5rem 1.25rem', overflowY: 'auto', flex: '1 1 auto' }}
                 data-testid="payment-review-items">
              {items.length === 0 ? (
                <div className="empty-state" style={{ padding: '1.5rem 0', color: 'hsl(var(--muted-foreground))' }}>
                  No line items to pay for.
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {items.map((it, i) => {
                    const isDiscount = it.kind === 'discount' || Number(it.subtotal) < 0;
                    return (
                      <li key={it.booking_id || `d-${i}`}
                          data-testid={`payment-review-item-${i}`}
                          data-kind={isDiscount ? 'discount' : 'booking'}
                          style={{
                            display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
                            columnGap: '0.75rem', padding: '0.65rem 0',
                            borderTop: i === 0 ? 'none' : '1px solid hsl(var(--border))',
                            alignItems: 'baseline',
                            color: isDiscount ? 'hsl(140 55% 30%)' : 'inherit',
                          }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600, fontSize: '0.95rem',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {isDiscount ? '🎁 ' : ''}{it.child_name}{it.session_label ? ` · ${it.session_label}` : ''}
                          </div>
                          <div style={{
                            fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: 2,
                          }}>{it.session_date || (isDiscount ? '' : '—')}</div>
                        </div>
                        <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                             data-testid={`payment-review-price-${i}`}>
                          {isDiscount ? '−' : ''}{symbol}{Math.abs(Number(it.subtotal || 0)).toFixed(2)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div style={{
              padding: '0.85rem 1.25rem 1rem',
              borderTop: '1px solid hsl(var(--border))',
              background: 'hsl(var(--muted) / 0.35)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                marginBottom: '0.75rem',
              }}>
                <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>Total</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                      data-testid="payment-review-total">{symbol}{total.toFixed(2)}</span>
              </div>
              {error && (
                <div className="card" style={{
                  borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
                  fontSize: '0.85rem', marginBottom: '0.65rem', padding: '0.5rem 0.75rem',
                }} data-testid="payment-review-error">{error}</div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" onClick={handleCardPay}
                        disabled={busy || items.length === 0}
                        className="btn btn-primary" data-testid="payment-review-pay-card"
                        style={{ flex: 1, minWidth: 180 }}>
                  {busy ? 'Redirecting…' : `💳 Pay ${symbol}${total.toFixed(2)} by card`}
                </button>
                <button type="button" onClick={handleBankPay}
                        disabled={busy || items.length === 0}
                        className="btn btn-ghost" data-testid="payment-review-pay-bank">
                  🏦 Bank transfer
                </button>
              </div>
              <div style={{
                marginTop: '0.55rem', fontSize: '0.72rem',
                color: 'hsl(var(--muted-foreground))', lineHeight: 1.4,
              }}>
                One charge, one line on your statement, one receipt — covering every booking above.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, highlight }) {
  return (
    <div style={{ display: 'flex', padding: '0.35rem 0', gap: '0.75rem', fontSize: '0.88rem' }}>
      <div style={{ width: 130, color: 'hsl(var(--muted-foreground))', flex: '0 0 auto' }}>{label}</div>
      <div style={{
        flex: 1, fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
        fontWeight: highlight ? 700 : 500,
        letterSpacing: mono ? 0.5 : 0,
      }}>{value || '—'}</div>
    </div>
  );
}

export default PaymentReviewDialog;
